import { useState, useCallback } from "react";

/**
 * useFileUpload Hook
 * Manages file upload state and base64 conversion
 * Memoized for optimal performance
 * 
 * @returns {Object} File state and handlers
 */
export const useFileUpload = () => {
  const [file, setFile] = useState(null);

  // Memoized base64 conversion
  const convertBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  }, []);

  // Memoized file read handler
  const handleFileRead = useCallback(async (event) => {
    const uploadedFile = event?.target?.files[0];
    if (!uploadedFile) return;

    const base64 = await convertBase64(uploadedFile);
    setFile({
      base64: base64.replace("data:", "").replace(/^.+,/, ""),
      fileExtension: uploadedFile.name?.split(".")[0],
      fileName: uploadedFile.name,
    });
    event.target.value = "";
  }, [convertBase64]);

  // Memoized file removal handler
  const removeFile = useCallback(() => {
    setFile(null);
  }, []);

  return {
    file,
    handleFileRead,
    removeFile,
    hasFile: file && file.fileName,
  };
};
